import React from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ChevronDown,
  ChevronUp,
  Eye,
  ExternalLink,
  PlayCircle,
  Power,
  RefreshCcw,
  StopCircle,
  Undo2,
} from 'lucide-react';
import { DeploymentCurrentRecord, DeploymentRecord, DeploymentTargetRecord } from '../../types/deployment';
import {
  canRestartDeploymentRecord,
  formatDate,
  getDeploymentActivityAt,
  getRepositoryName,
  getServerName,
  statusColor,
} from './deploymentExplorerUtils';
import { DeploymentHistoryPanel, DeploymentHistoryState } from './DeploymentHistoryPanel';

interface CurrentDeploymentRowProps {
  deploymentRecord: DeploymentCurrentRecord;
  targets: DeploymentTargetRecord[];
  saving?: boolean;
  historyOpen: boolean;
  historyState?: DeploymentHistoryState;
  onToggleHistory: () => void;
  onLoadMoreHistory: () => void;
  onRefreshHistory: () => void;
  onOpenDeploymentDetails: (deploymentId: string) => void;
  onRefreshDeployment: (deploymentId: string) => void;
  onComposeStart: (deploymentId: string) => void;
  onComposeStop: (deploymentRecord: DeploymentRecord) => void;
  onOpenRestart: (deploymentRecord: DeploymentRecord) => void;
  onOpenRollback: (deploymentRecord: DeploymentRecord) => void;
}

const renderGitLabLink = (deploymentRecord: DeploymentRecord) => {
  if (!deploymentRecord.gitlab) {
    return null;
  }

  return (
    <Stack spacing={0.25}>
      {deploymentRecord.gitlab.jobName ? (
        deploymentRecord.gitlab.jobWebUrl ? (
          <Typography
            component="a"
            href={deploymentRecord.gitlab.jobWebUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="caption"
            color="primary"
            sx={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            {deploymentRecord.gitlab.jobName}
            <ExternalLink size={12} />
          </Typography>
        ) : (
          <Typography variant="caption">{deploymentRecord.gitlab.jobName}</Typography>
        )
      ) : null}
      {deploymentRecord.gitlab.pipelineWebUrl ? (
        <Typography
          component="a"
          href={deploymentRecord.gitlab.pipelineWebUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="caption"
          color="primary"
          sx={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
        >
          Pipeline #{deploymentRecord.gitlab.pipelineId || 'open'}
          <ExternalLink size={12} />
        </Typography>
      ) : null}
    </Stack>
  );
};

export const CurrentDeploymentRow: React.FC<CurrentDeploymentRowProps> = ({
  deploymentRecord,
  targets,
  saving = false,
  historyOpen,
  historyState,
  onToggleHistory,
  onLoadMoreHistory,
  onRefreshHistory,
  onOpenDeploymentDetails,
  onRefreshDeployment,
  onComposeStart,
  onComposeStop,
  onOpenRestart,
  onOpenRollback,
}) => {
  const restartAvailable = canRestartDeploymentRecord(deploymentRecord, targets);

  return (
    <Card variant="outlined" sx={{ px: 2, py: 1.5 }}>
      <Stack spacing={1.5}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          justifyContent="space-between"
          alignItems={{ lg: 'center' }}
          spacing={1.5}
        >
          <Stack spacing={0.75}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
              <Typography variant="body1" fontWeight={700}>
                {getRepositoryName(deploymentRecord.repository)}
              </Typography>
              <Chip size="small" variant="outlined" label={deploymentRecord.environment || '-'} />
              <Chip size="small" label={deploymentRecord.actionType} />
              <Chip
                size="small"
                color={statusColor(deploymentRecord.status)}
                label={deploymentRecord.status}
              />
              {deploymentRecord.historyCount > 0 ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${deploymentRecord.historyCount} historical`}
                />
              ) : null}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {getServerName(deploymentRecord.server)} • {deploymentRecord.requestedRelease?.displayVersion || 'No version recorded'} • Updated {formatDate(getDeploymentActivityAt(deploymentRecord))}
            </Typography>
            {renderGitLabLink(deploymentRecord)}
          </Stack>

          <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
            <Tooltip title="View deployment details">
              <span>
                <IconButton
                  size="small"
                  disabled={saving}
                  onClick={() => onOpenDeploymentDetails(deploymentRecord._id)}
                >
                  <Eye size={16} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Refresh deployment from GitLab">
              <span>
                <IconButton
                  size="small"
                  disabled={saving}
                  onClick={() => onRefreshDeployment(deploymentRecord._id)}
                >
                  <RefreshCcw size={16} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={restartAvailable ? 'Start containers via GitLab' : 'Compose start unavailable'}>
              <span>
                <IconButton
                  size="small"
                  color="success"
                  disabled={saving || !restartAvailable}
                  onClick={() => onComposeStart(deploymentRecord._id)}
                >
                  <PlayCircle size={16} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={restartAvailable ? 'Stop containers via GitLab' : 'Compose stop unavailable'}>
              <span>
                <IconButton
                  size="small"
                  color="warning"
                  disabled={saving || !restartAvailable}
                  onClick={() => onComposeStop(deploymentRecord)}
                >
                  <StopCircle size={16} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={restartAvailable ? 'Restart deployed Docker compose project' : 'Restart unavailable'}>
              <span>
                <IconButton
                  size="small"
                  disabled={saving || !restartAvailable}
                  onClick={() => onOpenRestart(deploymentRecord)}
                >
                  <Power size={16} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={deploymentRecord.rollbackEligible ? 'Rollback deployment' : 'Rollback unavailable'}>
              <span>
                <IconButton
                  size="small"
                  disabled={saving || !deploymentRecord.rollbackEligible}
                  onClick={() => onOpenRollback(deploymentRecord)}
                >
                  <Undo2 size={16} />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        <Divider />

        <Box>
          <Button
            size="small"
            variant="text"
            endIcon={historyOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            onClick={onToggleHistory}
          >
            {historyOpen ? 'Hide History' : 'View History'}
          </Button>
          <Collapse in={historyOpen} unmountOnExit>
            <DeploymentHistoryPanel
              historyState={historyState}
              onLoadMore={onLoadMoreHistory}
              onOpenDeploymentDetails={onOpenDeploymentDetails}
              onRefreshHistory={onRefreshHistory}
              saving={saving}
            />
          </Collapse>
        </Box>
      </Stack>
    </Card>
  );
};
